import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createConfirmationFocusCoordinator } from './confirmation-dialog-state.js';
import { createActionFormRequestCoordinator } from './action-form-dialog-state.js';

const DIALOG_TONES = new Set(['accent', 'warning', 'danger']);
const FIELD_TYPES = new Set(['text', 'number', 'password']);
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function textOption(value, fallback) {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function normalizeField(field, index) {
  const name = textOption(field?.name, `field_${index + 1}`);
  return Object.freeze({
    name,
    label: textOption(field?.label, name),
    value: field?.value === null || field?.value === undefined ? '' : String(field.value),
    type: FIELD_TYPES.has(field?.type) ? field.type : 'text',
    multiline: field?.multiline === true,
    required: field?.required === true,
    integer: field?.integer === true,
    min: Number.isFinite(field?.min) ? field.min : undefined,
    max: Number.isFinite(field?.max) ? field.max : undefined,
    step: field?.step,
    help: typeof field?.help === 'string' ? field.help : '',
    placeholder: typeof field?.placeholder === 'string' ? field.placeholder : '',
    autoComplete: typeof field?.autoComplete === 'string' ? field.autoComplete : 'off',
  });
}

function dialogOptions(options = {}) {
  const fields = Array.isArray(options.fields) ? options.fields.map(normalizeField) : [];
  return Object.freeze({
    eyebrow: textOption(options.eyebrow, 'Action details'),
    title: textOption(options.title, 'Provide action details'),
    description: textOption(options.description, 'Complete the labelled fields before continuing.'),
    submitLabel: textOption(options.submitLabel, 'Continue'),
    cancelLabel: textOption(options.cancelLabel, 'Cancel'),
    tone: DIALOG_TONES.has(options.tone) ? options.tone : 'accent',
    initialFocus: options.initialFocus === 'cancel' ? 'cancel' : 'field',
    fields: Object.freeze(fields),
  });
}

function activeInvoker() {
  if (typeof document === 'undefined') return null;
  const element = document.activeElement;
  if (
    !element
    || element === document.body
    || element === document.documentElement
    || typeof element.focus !== 'function'
  ) return null;
  return element;
}

function focusElement(element) {
  if (!element || element.isConnected === false || typeof element.focus !== 'function') return;
  try {
    element.focus({ preventScroll: true });
  } catch {
    element.focus();
  }
}

function initialValues(fields) {
  return Object.fromEntries(fields.map((field) => [field.name, field.value]));
}

export function validateActionForm(fields, values) {
  const errors = {};
  for (const field of fields) {
    const value = String(values?.[field.name] ?? '').trim();
    if (field.required && !value) {
      errors[field.name] = `${field.label} is required.`;
      continue;
    }
    if (field.type !== 'number' || !value) continue;
    const number = Number(value);
    if (!Number.isFinite(number)) {
      errors[field.name] = `${field.label} must be a number.`;
    } else if (field.integer && !Number.isInteger(number)) {
      errors[field.name] = `${field.label} must be a whole number.`;
    } else if (field.min !== undefined && number < field.min) {
      errors[field.name] = `${field.label} must be at least ${field.min}.`;
    } else if (field.max !== undefined && number > field.max) {
      errors[field.name] = `${field.label} must be no more than ${field.max}.`;
    }
  }
  return errors;
}

function ActionFormDialogRequest({
  open = false,
  requestId = 0,
  eyebrow = 'Action details',
  title = 'Provide action details',
  description = 'Complete the labelled fields before continuing.',
  submitLabel = 'Continue',
  cancelLabel = 'Cancel',
  tone = 'accent',
  fields = [],
  initialFocus = 'field',
  returnFocus = null,
  onSubmit,
  onCancel,
}) {
  const generatedId = useId().replaceAll(':', '');
  const titleId = `bl-action-form-${generatedId}-title`;
  const descriptionId = `bl-action-form-${generatedId}-description`;
  const dialogRef = useRef(null);
  const firstFieldRef = useRef(null);
  const cancelRef = useRef(null);
  const submittedRef = useRef(false);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const [values, setValues] = useState(() => initialValues(fields));
  const [errors, setErrors] = useState({});
  const safeTone = DIALOG_TONES.has(tone) ? tone : 'accent';

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const dialog = dialogRef.current;
    const restoreTarget = returnFocus;
    focusElement(initialFocus === 'cancel' ? cancelRef.current : (firstFieldRef.current || dialog));

    function focusableElements() {
      return [...(dialog?.querySelectorAll(FOCUSABLE_SELECTOR) || [])]
        .filter((element) => element.getAttribute('aria-hidden') !== 'true');
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCancelRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = focusableElements();
      if (!controls.length) {
        event.preventDefault();
        focusElement(dialog);
        return;
      }
      const first = controls[0];
      const last = controls[controls.length - 1];
      const active = document.activeElement;
      if (!dialog?.contains(active)) {
        event.preventDefault();
        focusElement(event.shiftKey ? last : first);
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        focusElement(last);
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        focusElement(first);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      focusElement(restoreTarget);
    };
  }, [initialFocus, open, requestId, returnFocus]);

  if (!open) return null;

  function updateField(name, value) {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (submittedRef.current) return;
    const nextErrors = validateActionForm(fields, values);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      const firstInvalid = fields.find((field) => nextErrors[field.name]);
      const invalidControl = [...(dialogRef.current?.querySelectorAll('[name]') || [])]
        .find((control) => control.getAttribute('name') === firstInvalid?.name);
      focusElement(invalidControl);
      return;
    }
    submittedRef.current = true;
    onSubmit?.(Object.fromEntries(
      Object.entries(values).map(([name, value]) => [name, typeof value === 'string' ? value.trim() : value]),
    ));
  }

  return (
    <div className="bl-confirmation-backdrop" data-testid="action-form-backdrop">
      <section
        ref={dialogRef}
        className={`bl-confirmation-dialog bl-action-form-dialog is-${safeTone}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        data-tone={safeTone}
        tabIndex={-1}
      >
        <div className="bl-confirmation-dialog-copy">
          {eyebrow ? <div className="bl-confirmation-eyebrow">{eyebrow}</div> : null}
          <h2 id={titleId}>{title}</h2>
          <p id={descriptionId}>{description}</p>
        </div>
        <form className="bl-action-form" onSubmit={handleSubmit} noValidate>
          <div className="bl-action-form-fields">
            {fields.map((field, index) => {
              const fieldId = `bl-action-form-${generatedId}-${field.name}`;
              const errorId = `${fieldId}-error`;
              const helpId = `${fieldId}-help`;
              const describedBy = [field.help ? helpId : '', errors[field.name] ? errorId : ''].filter(Boolean).join(' ') || undefined;
              const commonProps = {
                ref: index === 0 ? firstFieldRef : undefined,
                id: fieldId,
                name: field.name,
                value: values[field.name] ?? '',
                required: field.required,
                placeholder: field.placeholder,
                autoComplete: field.autoComplete,
                'aria-invalid': errors[field.name] ? 'true' : undefined,
                'aria-describedby': describedBy,
                onChange: (event) => updateField(field.name, event.target.value),
              };
              return (
                <div className="bl-action-form-field" key={field.name}>
                  <label htmlFor={fieldId}>
                    {field.label}{field.required ? <span aria-hidden="true"> *</span> : null}
                  </label>
                  {field.multiline ? (
                    <textarea {...commonProps} rows={4} />
                  ) : (
                    <input {...commonProps} type={field.type} min={field.min} max={field.max} step={field.step} />
                  )}
                  {field.help ? <div className="bl-action-form-help" id={helpId}>{field.help}</div> : null}
                  {errors[field.name] ? <div className="bl-action-form-error" id={errorId} role="alert">{errors[field.name]}</div> : null}
                </div>
              );
            })}
          </div>
          <div className="bl-confirmation-dialog-actions">
            <button ref={cancelRef} type="button" className="bl-confirmation-button is-cancel" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button type="submit" className={`bl-confirmation-button is-confirm is-${safeTone}`}>
              {submitLabel}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export function ActionFormDialog(props) {
  if (!props.open) return null;
  const requestId = props.requestId ?? 0;
  return <ActionFormDialogRequest key={requestId} {...props} requestId={requestId} />;
}

export function useActionFormDialog() {
  const [dialog, setDialog] = useState(null);
  const coordinatorRef = useRef(null);
  const focusCoordinatorRef = useRef(null);
  const mountedRef = useRef(false);
  if (!coordinatorRef.current) coordinatorRef.current = createActionFormRequestCoordinator();
  if (!focusCoordinatorRef.current) focusCoordinatorRef.current = createConfirmationFocusCoordinator();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      coordinatorRef.current.cancel();
      focusCoordinatorRef.current.clear();
    };
  }, []);

  const settle = useCallback((requestId, value) => {
    const coordinator = coordinatorRef.current;
    if (!coordinator.isActive(requestId)) return false;
    const settled = coordinator.settle(requestId, value);
    if (!settled) return false;
    focusCoordinatorRef.current.clear();
    if (mountedRef.current) {
      setDialog((current) => current?.requestId === requestId ? null : current);
    }
    return true;
  }, []);

  const request = useCallback((options) => new Promise((resolve) => {
    const coordinator = coordinatorRef.current;
    const requestId = coordinator.replace(resolve);
    if (!mountedRef.current) {
      coordinator.settle(requestId, null);
      return;
    }
    setDialog({
      ...dialogOptions(options),
      requestId,
      returnFocus: focusCoordinatorRef.current.capture(activeInvoker),
    });
  }), []);

  const dialogProps = useMemo(() => {
    if (!dialog) return Object.freeze({ open: false });
    return Object.freeze({
      ...dialog,
      open: true,
      onCancel: () => settle(dialog.requestId, null),
      onSubmit: (values) => settle(dialog.requestId, values),
    });
  }, [dialog, settle]);

  return useMemo(() => Object.freeze({ request, dialogProps }), [dialogProps, request]);
}
